import React, { useState, useEffect, useCallback } from "react";
import {
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import CategoryIcon from "@material-ui/icons/Category";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const ACCENT = "#4F46E5";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  searchField: { marginRight: theme.spacing(2), minWidth: 300 },
  chip: { margin: theme.spacing(0.5) },
  statsCard: {
    textAlign: "center",
    borderLeft: `4px solid ${ACCENT}`,
  },
  statsValue: { fontSize: "2rem", fontWeight: 700, color: ACCENT },
  statsLabel: { fontSize: "0.75rem", color: "rgba(0,0,0,0.54)", textTransform: "uppercase", letterSpacing: "0.05em" },
  categoryChip: {
    fontWeight: 600,
    fontSize: "0.7rem",
  },
  articlePreview: {
    maxHeight: 400,
    overflow: "auto",
    padding: theme.spacing(2),
    backgroundColor: "#f9fafb",
    borderRadius: 8,
    whiteSpace: "pre-wrap",
    fontFamily: "inherit",
    lineHeight: 1.6,
  },
  headerAccent: { color: ACCENT },
}));

const CATEGORIES = [
  "Geral",
  "Configuração",
  "WhatsApp",
  "API",
  "Campanhas",
  "Automação",
  "Financeiro",
  "Integrações",
  "Troubleshooting",
];

const INITIAL_ARTICLES = [
  {
    id: 1,
    title: "Como conectar o WhatsApp Business",
    category: "WhatsApp",
    content: "Para conectar seu WhatsApp Business ao AtendeChat:\n\n1. Acesse Configurações > Conexões\n2. Clique em \"Nova Conexão\"\n3. Selecione \"WhatsApp\"\n4. Escaneie o QR Code com seu celular\n5. Aguarde a confirmação de conexão\n\nIMPORTANTE: Use um número exclusivo para o atendimento. Não use o mesmo número em outros apps de WhatsApp.\n\nEm caso de desconexão, vá em Conexões e clique em \"Reconectar\".",
    views: 342,
    status: "publicado",
    createdAt: "2026-03-01",
    updatedAt: "2026-03-28",
  },
  {
    id: 2,
    title: "Configurando respostas rápidas",
    category: "Automação",
    content: "As respostas rápidas agilizam o atendimento com mensagens pré-definidas.\n\nPara criar:\n1. Acesse Automação > Respostas Rápidas\n2. Clique em \"Adicionar\"\n3. Defina um atalho (ex: /saudacao)\n4. Escreva a mensagem\n5. Salve\n\nPara usar durante o atendimento, digite / seguido do atalho no campo de mensagem.\n\nVariáveis disponíveis:\n- {{nome}} - Nome do contato\n- {{protocolo}} - Número do ticket\n- {{fila}} - Nome da fila atual",
    views: 218,
    status: "publicado",
    createdAt: "2026-03-05",
    updatedAt: "2026-03-25",
  },
  {
    id: 3,
    title: "Guia de uso da API de Mensagens",
    category: "API",
    content: "A API de Mensagens permite enviar mensagens programaticamente.\n\nEndpoint: POST /api/messages/send\n\nHeaders:\n- Authorization: Bearer {seu_token}\n- Content-Type: application/json\n\nBody:\n{\n  \"number\": \"5511999999999\",\n  \"body\": \"Sua mensagem aqui\"\n}\n\nLimites:\n- 60 mensagens por minuto\n- Máximo 4096 caracteres por mensagem\n- Suporte a mídia via base64 ou URL\n\nPara gerar seu token, acesse Configurações > API de Mensagens.",
    views: 156,
    status: "publicado",
    createdAt: "2026-03-10",
    updatedAt: "2026-04-01",
  },
  {
    id: 4,
    title: "Criando fluxos de chatbot",
    category: "Automação",
    content: "O FlowBuilder permite criar fluxos de atendimento automatizado.\n\nPasso a passo:\n1. Acesse Automação > Fluxos (Chatbot)\n2. Clique em \"Novo Fluxo\"\n3. Arraste os blocos para a área de trabalho:\n   - Mensagem: Envia texto ao cliente\n   - Pergunta: Coleta resposta do cliente\n   - Condição: Direciona baseado na resposta\n   - Transferir: Encaminha para fila/agente\n4. Conecte os blocos com as setas\n5. Salve e ative o fluxo\n\nBoas práticas:\n- Limite a 5 níveis de profundidade\n- Sempre ofereça opção de falar com humano\n- Teste antes de ativar",
    views: 189,
    status: "publicado",
    createdAt: "2026-03-12",
    updatedAt: "2026-03-30",
  },
  {
    id: 5,
    title: "Configurando SLA e Prioridades",
    category: "Configuração",
    content: "O SLA (Service Level Agreement) define os prazos de atendimento.\n\nPara configurar:\n1. Acesse Equipe > Políticas SLA\n2. Crie uma nova política\n3. Defina:\n   - Tempo máximo para primeira resposta\n   - Tempo máximo para resolução\n   - Horário de funcionamento\n   - Prioridades (Urgente, Alta, Média, Baixa)\n4. Associe a política às filas desejadas\n\nOs tickets que violarem o SLA serão destacados em vermelho no painel.",
    views: 97,
    status: "publicado",
    createdAt: "2026-03-15",
    updatedAt: "2026-04-02",
  },
  {
    id: 6,
    title: "Importação e exportação de contatos",
    category: "Geral",
    content: "Para importar contatos em massa:\n1. Acesse Atendimento > Importar / Exportar\n2. Baixe o modelo CSV\n3. Preencha com os dados dos contatos\n4. Faça upload do arquivo\n5. Verifique a prévia e confirme\n\nPara exportar:\n1. Na mesma página, clique em \"Exportar\"\n2. Selecione os filtros desejados\n3. Clique em \"Gerar CSV\"\n\nFormato aceito: CSV com colunas nome, numero, email\nLimite: 10.000 contatos por importação",
    views: 134,
    status: "publicado",
    createdAt: "2026-03-18",
    updatedAt: "2026-03-28",
  },
  {
    id: 7,
    title: "Troubleshooting: WhatsApp desconectando",
    category: "Troubleshooting",
    content: "Se o WhatsApp está desconectando frequentemente:\n\n1. Verifique a conexão de internet do servidor\n2. Certifique-se que o celular está conectado à internet\n3. Não use o WhatsApp Web no navegador simultaneamente\n4. Verifique se a sessão não expirou (reconecte se necessário)\n5. Limpe o cache da conexão em Configurações > Conexões\n\nSe o problema persistir:\n- Desconecte e reconecte a sessão\n- Aguarde 5 minutos entre desconexão e reconexão\n- Verifique se não há atualizações pendentes do WhatsApp no celular",
    views: 267,
    status: "publicado",
    createdAt: "2026-03-20",
    updatedAt: "2026-04-03",
  },
  {
    id: 8,
    title: "Configurando campanhas de mensagens",
    category: "Campanhas",
    content: "Para criar uma campanha de mensagens em massa:\n\n1. Crie uma Lista de Contatos em Campanhas > Listas\n2. Adicione os contatos (manual ou importação)\n3. Acesse Campanhas e clique em \"Nova Campanha\"\n4. Selecione a lista de contatos\n5. Escreva a mensagem (suporta variáveis)\n6. Configure o agendamento\n7. Revise e ative\n\nDicas:\n- Respeite o limite de envio por hora\n- Personalize com {{nome}} para melhor engajamento\n- Acompanhe os resultados em tempo real no relatório da campanha",
    views: 145,
    status: "publicado",
    createdAt: "2026-03-22",
    updatedAt: "2026-04-01",
  },
  {
    id: 9,
    title: "Templates WhatsApp Business API",
    category: "WhatsApp",
    content: "Templates são mensagens pré-aprovadas pela Meta para iniciar conversas.\n\nPara criar um template:\n1. Acesse Automação > Templates WhatsApp\n2. Clique em \"Novo Template\"\n3. Preencha:\n   - Nome: identificador único (snake_case)\n   - Categoria: Marketing, Utility ou Authentication\n   - Idioma: pt_BR\n   - Corpo: texto com variáveis {{1}}, {{2}}\n4. Envie para aprovação da Meta\n\nPrazo de aprovação: 24-48 horas\nStatus: Aprovado, Rejeitado ou Em análise",
    views: 88,
    status: "rascunho",
    createdAt: "2026-04-01",
    updatedAt: "2026-04-05",
  },
  {
    id: 10,
    title: "Integrações com sistemas externos",
    category: "Integrações",
    content: "O AtendeChat suporta integrações via Webhook e API REST.\n\nWebhooks disponíveis:\n- Novo ticket criado\n- Ticket atualizado\n- Mensagem recebida\n- Contato criado/atualizado\n\nPara configurar:\n1. Acesse Automação > Integrações\n2. Clique em \"Novo Webhook\"\n3. Insira a URL de destino\n4. Selecione os eventos\n5. Teste a conexão\n6. Ative\n\nSistemas comumente integrados:\n- CRM (Pipedrive, HubSpot)\n- ERP (SAP, TOTVS)\n- E-commerce (Shopify, WooCommerce)\n- Helpdesk (Zendesk, Freshdesk)",
    views: 112,
    status: "publicado",
    createdAt: "2026-03-25",
    updatedAt: "2026-04-04",
  },
];

const KnowledgeBase = () => {
  const classes = useStyles();
  const [articles, setArticles] = useState(INITIAL_ARTICLES);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("Todas");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    category: "Geral",
    content: "",
    status: "rascunho",
  });

  const filtered = articles.filter((a) => {
    const matchSearch =
      a.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = categoryFilter === "Todas" || a.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const stats = {
    total: articles.length,
    published: articles.filter((a) => a.status === "publicado").length,
    categories: [...new Set(articles.map((a) => a.category))].length,
    totalViews: articles.reduce((sum, a) => sum + a.views, 0),
  };

  const handleOpen = (article) => {
    if (article) {
      setFormData({
        title: article.title,
        category: article.category,
        content: article.content,
        status: article.status,
      });
      setSelectedArticle(article);
    } else {
      setFormData({ title: "", category: "Geral", content: "", status: "rascunho" });
      setSelectedArticle(null);
    }
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Preencha título e conteúdo");
      return;
    }
    if (selectedArticle) {
      setArticles((prev) =>
        prev.map((a) =>
          a.id === selectedArticle.id
            ? { ...a, ...formData, updatedAt: new Date().toISOString().slice(0, 10) }
            : a
        )
      );
      toast.success("Artigo atualizado!");
    } else {
      const newArticle = {
        id: Math.max(...articles.map((a) => a.id)) + 1,
        ...formData,
        views: 0,
        createdAt: new Date().toISOString().slice(0, 10),
        updatedAt: new Date().toISOString().slice(0, 10),
      };
      setArticles((prev) => [...prev, newArticle]);
      toast.success("Artigo criado!");
    }
    setDialogOpen(false);
  };

  const handleDelete = (id) => {
    if (window.confirm("Tem certeza que deseja excluir este artigo?")) {
      setArticles((prev) => prev.filter((a) => a.id !== id));
      toast.success("Artigo excluído!");
    }
  };

  const handleView = (article) => {
    setSelectedArticle(article);
    setArticles((prev) =>
      prev.map((a) => (a.id === article.id ? { ...a, views: a.views + 1 } : a))
    );
    setViewDialogOpen(true);
  };

  const handleCopyContent = (content) => {
    navigator.clipboard.writeText(content);
    toast.success("Conteúdo copiado!");
  };

  const statusColor = (status) => {
    switch (status) {
      case "publicado": return "#22c55e";
      case "rascunho": return "#f59e0b";
      case "arquivado": return "#94a3b8";
      default: return "#6b7280";
    }
  };

  return (
    <div className={classes.root}>
      <MainHeader>
        <Title>Base de Conhecimento</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Buscar artigos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: ACCENT }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl variant="outlined" size="small" style={{ minWidth: 150, marginRight: 16 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              label="Categoria"
            >
              <MenuItem value="Todas">Todas</MenuItem>
              {CATEGORIES.map((c) => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            style={{ backgroundColor: ACCENT, color: "#fff" }}
            startIcon={<AddIcon />}
            onClick={() => handleOpen(null)}
          >
            Novo Artigo
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Stats cards */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        {[
          { label: "Total de Artigos", value: stats.total, icon: <MenuBookIcon /> },
          { label: "Publicados", value: stats.published, icon: <VisibilityIcon /> },
          { label: "Categorias", value: stats.categories, icon: <CategoryIcon /> },
          { label: "Visualizações", value: stats.totalViews.toLocaleString(), icon: <TrendingUpIcon /> },
        ].map((s, i) => (
          <Grid item xs={3} key={i}>
            <Card className={classes.statsCard}>
              <CardContent>
                <Typography className={classes.statsValue}>{s.value}</Typography>
                <Typography className={classes.statsLabel}>{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper className={classes.paper}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 700 }}>Título</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Categoria</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Visualizações</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Atualizado</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((article) => (
                <TableRow key={article.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 600, cursor: "pointer", color: ACCENT }}
                      onClick={() => handleView(article)}
                    >
                      {article.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={article.category}
                      size="small"
                      className={classes.categoryChip}
                      style={{ backgroundColor: `${ACCENT}15`, color: ACCENT }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={article.status}
                      size="small"
                      style={{
                        backgroundColor: `${statusColor(article.status)}20`,
                        color: statusColor(article.status),
                        fontWeight: 600,
                        fontSize: "0.7rem",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">{article.views}</TableCell>
                  <TableCell>{article.updatedAt}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Visualizar">
                      <IconButton size="small" onClick={() => handleView(article)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copiar conteúdo">
                      <IconButton size="small" onClick={() => handleCopyContent(article.content)}>
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpen(article)}>
                        <EditIcon fontSize="small" style={{ color: ACCENT }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => handleDelete(article.id)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: 32 }}>
                    <Typography color="textSecondary">
                      Nenhum artigo encontrado
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle style={{ color: ACCENT }}>
          {selectedArticle ? "Editar Artigo" : "Novo Artigo"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Título"
            variant="outlined"
            margin="dense"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          />
          <Box display="flex" gap={2} mt={1} mb={1}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 200, marginRight: 16 }}>
              <InputLabel>Categoria</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                label="Categoria"
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Status"
              >
                <MenuItem value="rascunho">Rascunho</MenuItem>
                <MenuItem value="publicado">Publicado</MenuItem>
                <MenuItem value="arquivado">Arquivado</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            label="Conteúdo"
            variant="outlined"
            margin="dense"
            multiline
            rows={15}
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            style={{ backgroundColor: ACCENT, color: "#fff" }}
            onClick={handleSave}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" style={{ color: ACCENT, fontWeight: 700 }}>
              {selectedArticle?.title}
            </Typography>
            <Chip
              label={selectedArticle?.category}
              size="small"
              style={{ backgroundColor: `${ACCENT}15`, color: ACCENT, fontWeight: 600 }}
            />
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2} display="flex" gap={2}>
            <Typography variant="caption" color="textSecondary">
              Criado: {selectedArticle?.createdAt}
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 16 }}>
              Atualizado: {selectedArticle?.updatedAt}
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ marginLeft: 16 }}>
              {selectedArticle?.views} visualizações
            </Typography>
          </Box>
          <div className={classes.articlePreview}>
            {selectedArticle?.content}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<FileCopyIcon />}
            onClick={() => handleCopyContent(selectedArticle?.content || "")}
          >
            Copiar
          </Button>
          <Button onClick={() => setViewDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default KnowledgeBase;
